// @ts-nocheck
import { useState } from "react";
import UserDisplay from "@/components/shared/UserDisplay";
import PrintDialog from "@/components/shared/PrintDialog";
import { useAuthStore } from "@/stores/auth.store";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import RelatedDataTabs from "@/components/shared/RelatedDataTabs";
import { ConfigLabel } from "@/components/shared/ConfigLabel";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { EntityLink } from "@/components/shared/EntityLink";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Printer,
  Truck,
  CreditCard,
  Factory,
  Package,
  Plus,
  FileText,
  Eye,
  ExternalLink,
  MoreHorizontal,
  PlayCircle,
  CheckCircle2,
  History,
  Paperclip,
  Layers,
  TrendingUp,
  RotateCcw,
  ChevronDown,
} from "lucide-react";

import { OrderDetailImpositionModal } from "./components/OrderDetailImpositionModal";
import { CreateProductionOrderDialog } from "./components/CreateProductionOrderDialog";
import { ActionGuard } from "@/components/shared/ActionGuard";
import { RePrepressRequestModal } from "@/pages/planning/production-orders/components/RePrepressRequestModal";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { trpcClient } from "@/lib/trpc";
import { ordersApi } from "@/lib/api-client";
import { formatMoney, formatDate, formatNumber, cn } from "@/lib/utils";
import { getStatusConfig } from "@/config/status-registry";
import { evaluateDeliveryTolerance } from "@apv-erp/status";
import WorkflowStepper from "@/components/shared/WorkflowStepper";
import { useWorkflowSteps } from "@/hooks/useWorkflowSteps";
import { toast } from "sonner";
import {
  OrderDeliveriesTab,
  OrderProductionOrdersTab,
  OrderPaymentsTab,
  OrderPrepressTab,
} from "./components/OrderTabs";
import { OrderSettlementTab } from "./components/OrderSettlementTab";
import { ZIndexView, useZIndexView } from "@/components/shared/ZIndexView";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { useConfirm } from "@/providers/ConfirmProvider";
import DetailLoadingSkeleton from "@/components/shared/DetailLoadingSkeleton";
import { formatProductSpecs } from "@/lib/format-specs";
import { orderLineDetailText } from "@/lib/order-detail-text";
import ImageLightbox from "@/components/shared/ImageLightbox";
import { DetailField as F } from "@/components/shared/DetailField";

const formatSpecs = (specsRaw: any, product?: any): string => formatProductSpecs(specsRaw, product);

function cleanProcessText(processText: string, specsText: string): string {
  if (!processText) return "";
  let cleaned = processText.replace(/\+/g, ", ");

  // 1. Remove duplicate specs from process
  if (specsText) {
    const escapedSpecs = specsText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const specRegex = new RegExp(`(?:,\\s*)?${escapedSpecs}(?:\\s*,)?`, "gi");
    cleaned = cleaned
      .replace(specRegex, ", ")
      .replace(/,\s*$/, "")
      .replace(/^,\s*/, "")
      .trim();
  }

  // 2. Format material names if they match pattern like "I350 Ivory" -> "Ivory 350gsm"
  cleaned = cleaned.replace(
    /(^|,\s*)([A-Z])(\d{2,3})\s+([a-zA-Z0-9_-]+)/gi,
    (match, prefix, letter, weight, name) => {
      return `${prefix}${name} ${weight}gsm`;
    },
  );

  return cleaned;
}

const fmt = (v: number | null | undefined, cur?: string) => formatMoney(v, cur);
const fmtD = (d: string | Date | null | undefined) => formatDate(d) || "—";


// ── KPI Stat (compact) ────────────────────────────────────────
const Stat = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white border text-sm">
    <span className="text-slate-700 text-xs">{label}</span>
    <span className={`font-bold tabular-nums ${color || "text-slate-900"}`}>
      {value}
    </span>
  </div>
);

/** Dòng đơn hàng còn SL cần lên lệnh (cùng cách tính với danh sách chờ lên lệnh / máy chủ) */
const openItemsOf = (items: any[]) =>
  items.filter((it: any) => {
    const rem = it.remainingPlanQty != null && Number(it.remainingPlanQty) > 0
      ? Number(it.remainingPlanQty)
      : Math.max(0, Number(it.quantity || 0) - Number(it.plannedQty || 0));
    return rem > 0;
  });

export default function OrderView({
  orderId: propId,
  isZIndex,
}: {
  orderId?: string;
  isZIndex?: boolean;
}) {
  const confirm = useConfirm();
  const params = useParams({ strict: false }) as any;
  const orderId = propId || params.orderId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [printType, setPrintType] = useState<"order" | "production">("order");
  const [showPoForm, setShowPoForm] = useState(false);
  const [poInitialData, setPoInitialData] = useState<any>(null);
  const [selectedDetailIds, setSelectedDetailIds] = useState<string[]>([]);
  const [impositionDetail, setImpositionDetail] = useState<any | null>(null);
  const [showImpositionModal, setShowImpositionModal] = useState(false);
  const [showCreatePoDialog, setShowCreatePoDialog] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [rePrepressModalOpen, setRePrepressModalOpen] = useState(false);
  const [rePrepressTargetItems, setRePrepressTargetItems] = useState<any[]>([]);
  const [groupByLayout, setGroupByLayout] = useState(false);

  const handleOpenImposition = (detail: any) => {
    setImpositionDetail(detail);
    setShowImpositionModal(true);
  };
  const { canEditOrder, hasPermission } = useRoleAccess();
  const { workflow } = useWorkflowSteps("order");
  const zIndex = useZIndexView();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [pendingStatus, setPendingStatus] = useState("");
  // ── Main order data ──
  const {
    data: orderData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["order", orderId],
    queryFn: async () => trpcClient.orders.getById.query({ id: orderId }),
    staleTime: 0,
    refetchOnMount: "always",
  });

  const order = orderData as any;

  const handleUpdateDetailStatus = async (detailId: string, status: string) => {
    try {
      await trpcClient.orders.updateDetailStatus.mutate({
        id: detailId,
        status,
      });

      toast.success("Đã cập nhật trạng thái chi tiết sản phẩm!");
      await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
    } catch (e: any) {
      toast.error(e.message || "Lỗi cập nhật trạng thái");
    }
  };

  const handleBulkUpdateDetailStatus = async (detailIds: string[], status: string) => {
    try {
      await Promise.all(
        detailIds.map((id) =>
          trpcClient.orders.updateDetailStatus.mutate({ id, status })
        )
      );
      toast.success(`Đã cập nhật trạng thái cho ${detailIds.length} chi tiết sản phẩm!`);
      await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      setSelectedDetailIds([]);
    } catch (e: any) {
      toast.error(e.message || "Lỗi khi cập nhật trạng thái hàng loạt");
    }
  };

  const handleBulkConfirmPrepress = async (detailIds: string[]) => {
    const selectedItems = (order?.orderDetails || (order as any)?.details || []).filter((d: any) =>
      detailIds.includes(d.id)
    );
    const unassigned = selectedItems.filter((d: any) => !d.prepressCode);
    if (unassigned.length > 0) {
      toast.error(
        `Có ${unassigned.length} chi tiết chưa có thông số bài bình. Bắt buộc phải hoàn tất bình file trước khi xác nhận!`,
        {
          action: {
            label: "Bình file ngay",
            onClick: () => handleOpenImposition(unassigned[0]),
          },
        }
      );
      return;
    }
    await handleBulkUpdateDetailStatus(detailIds, "PREPRESS_PLANNED");
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await trpcClient.orders.delete.mutate({ id: orderId });

      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      navigate({ to: "/orders" });
    } catch (err: any) {
      toast.error(err?.message || "Xoá thất bại");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (newStatus === "CANCELLED") {
      setPendingStatus(newStatus);
      setCancelReason("");
      setShowCancelDialog(true);
      return;
    }
    await executeStatusChange(newStatus);
  };

  const executeStatusChange = async (newStatus: string, reason?: string) => {
    // Use dedicated REST endpoint which handles server-side activity logging
    const userId = useAuthStore.getState().user?.id;
    await ordersApi.updateStatus(order.id, newStatus, userId || undefined);

    if (reason) {
      try {
        await trpcClient.comments.create.mutate({
          entityType: "order",
          entityId: order.id,
          content: `Lý do hủy: ${reason}`,
        });
      } catch (e) {
        console.error("Failed to add cancel reason comment", e);
      }
    }

    await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
    await queryClient.invalidateQueries({ queryKey: ["activity-log"] });
    await queryClient.invalidateQueries({ queryKey: ["orders"] });
    await queryClient.invalidateQueries({
      queryKey: ["activity-log", "order", order.id],
    });
  };

  const handleApproveOrder = async () => {
    const isMissingTax = !order?.customer?.taxCode?.trim();
    const isMissingAddr = !order?.customer?.address?.trim();
    if (isMissingTax || isMissingAddr) {
      const missingList = [isMissingTax ? "Mã số thuế (MST)" : null, isMissingAddr ? "Địa chỉ" : null].filter(Boolean);
      toast.error(
        `Không thể duyệt: Khách hàng "${order.customer?.name || order.customer?.code}" chưa có thông tin bắt buộc (${missingList.join(", ")}). Vui lòng cập nhật thông tin khách hàng trước khi duyệt!`
      );
      return;
    }
    setIsApproving(true);
    try {
      await executeStatusChange("CONFIRMED");
      toast.success(`Đã duyệt đơn hàng ${order.orderId} thành công!`);
      setShowApproveConfirm(false);
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi duyệt đơn hàng");
    } finally {
      setIsApproving(false);
    }
  };

  // --- Loading / Error ---
  if (isLoading) {
    return <DetailLoadingSkeleton breadcrumbs={[{ label: "Đơn hàng", to: "/orders" }]} />;
  }

  if (error || !order) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Đơn hàng", to: "/orders" }]}
        title="Không tìm thấy"
      >
        <div className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            Không tìm thấy đơn hàng
          </h3>
          <Button onClick={() => navigate({ to: "/orders" })}>
            Quay lại danh sách
          </Button>
        </div>
      </StandardDetailLayout>
    );
  }

  // ── Computed from REAL schema fields ──
  const statusCfg = getStatusConfig("OrderStatus", order.status);
  const details: any[] = order.orderDetails || [];

  // Customer legal compliance check (tax code and legal address)
  const isMissingCustomerTax = !order.customer?.taxCode?.trim();
  const isMissingCustomerAddress = !order.customer?.address?.trim();
  const hasCustomerMissingInfo = isMissingCustomerTax || isMissingCustomerAddress;
  const missingCustomerFields = [
    isMissingCustomerTax ? "Mã số thuế (MST)" : null,
    isMissingCustomerAddress ? "Địa chỉ" : null,
  ].filter(Boolean);

  // Line total from details (unitPrice * quantity)
  const lineTotal = details.reduce(
    (s: number, d: any) =>
      s +
      Number(d.quantity || 0) *
        Number(order.currency !== "VND" ? d.priceUSD || 0 : d.unitPrice || 0),
    0,
  );
  const shippingFee = Number(order.shippingFee || 0);
  const vatRate = Number(order.vatRate || 0);
  const calcVatAmount = lineTotal * (vatRate / 100);

  // Recalculate robustly in view
  const vatAmount =
    order.vatAmount !== undefined && order.vatAmount !== null
      ? Number(order.vatAmount)
      : calcVatAmount;
  const totalAmount = lineTotal + vatAmount + shippingFee;

  // Delivery tolerance (±5% default or order/customer-configured)
  const orderTolerancePercent = Math.max(
    Number(order.tolerancePercent || 0),
    Number(order.customer?.tolerancePercent || 0),
    5,
  );
  const totalOrderedQty = details.reduce((s: number, d: any) => s + Number(d.quantity || 0), 0);
  const totalDeliveredQty = details.reduce((s: number, d: any) => s + Number(d.qtyDelivered || 0), 0);
  const orderToleranceEval = evaluateDeliveryTolerance(
    totalOrderedQty,
    totalDeliveredQty,
    orderTolerancePercent,
  );

  // Logic for showing Production/Outsource buttons
  const isNewOrder = ["DRAFT", "PENDING"].includes(order.status);
  const isOutsourceGroup = details.some(
    (d: any) =>
      d.product?.productGroup?.code === "OUTSOURCE" ||
      d.product?.productGroup?.name?.toLowerCase().includes("gia công"),
  );
  const showProductionButtons =
    ["DRAFT", "PENDING", "CONFIRMED", "APPROVED", "PLANNED", "IN_PRODUCTION"].includes(
      order.status,
    ) || isOutsourceGroup;

  return (
    <>
      <StandardDetailLayout
        isZIndex={isZIndex}
        breadcrumbs={[{ label: "Đơn hàng", to: "/orders" }]}
        title={order.orderId}
        entityId={order.id}
        entityType="order"
        timestamps={{
          created: order.createdAt,
          updated: order.updatedAt,
          createdBy: order.salesUser?.fullName || null,
          updatedBy:
            order.updatedByUser?.fullName || order.salesUser?.fullName || null,
        }}
        status={
          <div className="flex items-center gap-1.5 flex-wrap">
            <Badge variant="outline" className={statusCfg.color}>
              {statusCfg.label || order.status}
            </Badge>
            {((order.status === "COMPLETED" || (order.completionPercent ?? 0) >= 100) && order.status !== "DELIVERED" && order.status !== "CANCELLED") && (
              <Badge className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold text-xs px-2 py-0.5 border-0 flex items-center gap-1 shadow-xs">
                <Truck className="w-3 h-3" /> SẴN SÀNG XUẤT GIAO
              </Badge>
            )}
            {totalOrderedQty > 0 && (
              <Badge
                variant="outline"
                data-testid="order-delivery-tolerance-badge"
                className={cn(
                  "text-[11px] font-semibold px-2 py-0.5",
                  orderToleranceEval.isWithinTolerance
                    ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                    : totalDeliveredQty > 0
                      ? "bg-amber-50 text-amber-800 border-amber-300"
                      : "bg-slate-50 text-slate-600 border-slate-200",
                )}
              >
                Giao: {formatNumber(totalDeliveredQty)}/{formatNumber(totalOrderedQty)} (Dung sai ±{orderTolerancePercent}%)
              </Badge>
            )}
          </div>
        }
        actions={
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5 w-auto">
            {hasPermission("orders", "update") &&
              (["CONFIRMED", "APPROVED", "PLANNED", "IN_PRODUCTION", "DELIVERED"].includes(order.status) ||
                // Đơn đã giao đủ nhưng đang Hoàn thành / Đã xuất HĐ: chốt quyết toán sang Đã đóng (FB-9TUVMG)
                (["COMPLETED", "INVOICED"].includes(order.status) && orderToleranceEval.isWithinTolerance)) && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="btn-close-order-tolerance"
                  className={cn(
                    "h-8 px-2 sm:px-3 text-[11px] sm:text-xs font-medium shrink-0 whitespace-nowrap",
                    orderToleranceEval.isWithinTolerance
                      ? "text-emerald-700 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 font-semibold"
                      : "text-emerald-600 border-emerald-200 hover:bg-emerald-50",
                  )}
                  onClick={async () => {
                    if (
                      !(await confirm(
                        orderToleranceEval.isWithinTolerance
                          ? `Đơn hàng ${order.orderId} đã giao ${formatNumber(totalDeliveredQty)}/${formatNumber(totalOrderedQty)} SP (đạt dung sai ±${orderTolerancePercent}%). Bạn có chắc muốn chốt hoàn tất đơn hàng?`
                          : `Bạn có chắc muốn chốt đơn hàng ${order.orderId}?`,
                      ))
                    )
                      return;
                    try {
                      if (orderToleranceEval.isWithinTolerance) {
                        await trpcClient.orders.closeOrderWithinTolerance.mutate({
                          id: order.id,
                        });
                      } else {
                        await trpcClient.orders.updateStatus.mutate({
                          id: order.id,
                          status: "COMPLETED",
                        });
                      }
                      toast.success(
                        orderToleranceEval.isWithinTolerance
                          ? `Đã chốt hoàn tất đơn hàng ${order.orderId} — trạng thái Đã đóng`
                          : `Đã chốt hoàn tất đơn hàng ${order.orderId}`,
                      );
                      queryClient.invalidateQueries({
                        queryKey: ["order", orderId],
                      });
                      queryClient.invalidateQueries({ queryKey: ["orders"] });
                      queryClient.invalidateQueries({
                        queryKey: ["order-settlement", orderId],
                      });
                    } catch (err: any) {
                      toast.error(err?.message || "Lỗi khi chốt đơn");
                    }
                  }}
                >
                  <CheckCircle2 className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" />{" "}
                  {orderToleranceEval.isWithinTolerance
                    ? `Chốt hoàn tất (Dung sai ±${orderTolerancePercent}%)`
                    : "Chốt đơn"}
                </Button>
              )}

            {/* ── Duyệt Đơn Hàng (Order Approval) ── */}
            {["DRAFT", "PENDING"].includes(order.status) && (
              <ActionGuard
                module="orders"
                action="approve"
                fallbackMessage="Chỉ Giám Đốc hoặc Trưởng phòng Kinh Doanh mới có quyền duyệt Đơn hàng này"
              >
                <Button
                  size="sm"
                  className={cn(
                    "text-white h-8 px-2 sm:px-3 text-[11px] sm:text-xs font-semibold shrink-0 whitespace-nowrap shadow-xs",
                    hasCustomerMissingInfo
                      ? "bg-amber-600 hover:bg-amber-700 border border-amber-500"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  )}
                  onClick={() => setShowApproveConfirm(true)}
                  title={hasCustomerMissingInfo ? `Khách hàng thiếu ${missingCustomerFields.join(", ")}` : "Duyệt Đơn"}
                >
                  <CheckCircle2 className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" />
                  {hasCustomerMissingInfo ? "Duyệt Đơn (Thiếu MST/Đ/c)" : "Duyệt Đơn"}
                </Button>
              </ActionGuard>
            )}

            {/* ── In đơn hàng: mọi trạng thái (nút in luôn hiện) ── */}
            <Button
              variant="outline"
              size="sm"
              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 h-8 px-2 sm:px-3 text-[11px] sm:text-xs font-medium shrink-0 whitespace-nowrap"
              onClick={async () => {
                setPrintType("order");
                setShowPrint(true);
              }}
            >
              <Printer className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> In ĐH
            </Button>

            {/* ── Create Production Order from Order ── */}
            {showProductionButtons && (
              <>
                <Button
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 px-2 sm:px-3 text-[11px] sm:text-xs font-semibold shrink-0 whitespace-nowrap shadow-xs"
                  onClick={() => setShowCreatePoDialog(true)}
                >
                  <Factory className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> Lập Lệnh SX
                </Button>

                {details.some((d: any) => !d.prepressCode) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-purple-700 border-purple-300 bg-purple-50 hover:bg-purple-100 h-8 px-2 sm:px-3 text-[11px] sm:text-xs font-medium shrink-0 whitespace-nowrap"
                    onClick={() => {
                      const unassignedIds = details.filter((d: any) => !d.prepressCode).map((d: any) => d.id).join(",");
                      navigate({
                        to: "/production/prepress/create" as any,
                        search: { orderDetailIds: unassignedIds } as any,
                      });
                    }}
                  >
                    <Layers className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> Bình file
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="text-orange-600 border-orange-200 hover:bg-orange-50 h-8 px-2 sm:px-3 text-[11px] sm:text-xs font-medium shrink-0 whitespace-nowrap"
                  onClick={() =>
                    navigate({
                      to: "/outsource-orders/create",
                      search: { orderId: order.id },
                    } as any)
                  }
                >
                  <Factory className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> Đặt GC
                </Button>
              </>
            )}

            {/* ── Create Delivery from Order ── */}
            {["APPROVED", "CONFIRMED", "IN_PRODUCTION", "COMPLETED", "DELIVERED"].includes(
              order.status,
            ) && (
              <Button
                size="sm"
                className="bg-teal-600 hover:bg-teal-700 text-white h-8 px-2 sm:px-3 text-[11px] sm:text-xs font-medium shrink-0 whitespace-nowrap"
                onClick={() =>
                  navigate({
                    to: "/delivery/create",
                    search: {
                      orderId: order.id,
                      customerId: order.customerId,
                    } as any,
                  })
                }
              >
                <Truck className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> Tạo PGH
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="text-blue-600 border-blue-200 hover:bg-blue-50 h-8 px-2 sm:px-3 text-[11px] sm:text-xs font-medium shrink-0 whitespace-nowrap"
              onClick={async () => {
                setPrintType("production");
                setShowPrint(true);
              }}
            >
              <Printer className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> In YCSX
            </Button>
            {!isZIndex && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2 sm:px-3 text-[11px] sm:text-xs font-medium shrink-0 whitespace-nowrap"
                onClick={() =>
                  navigate({ to: "/orders/$orderId/edit", params: { orderId } })
                }
                disabled={!canEditOrder(order.status)}
                title={
                  !canEditOrder(order.status)
                    ? "Bạn không có quyền sửa đơn hàng ở trạng thái này"
                    : undefined
                }
              >
                <Pencil className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> Sửa
              </Button>
            )}
            {canEditOrder(order.status) && (
              <Button
                variant="outline"
                size="sm"
                className="text-rose-600 border-rose-200 hover:bg-rose-50 h-8 px-2 sm:px-3 text-[11px] sm:text-xs font-medium shrink-0 whitespace-nowrap"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> Xoá
              </Button>
            )}
          </div>
        }
        workflow={
          <WorkflowStepper
            workflow={workflow}
            currentStatus={order.status}
            onStatusChange={handleStatusChange}
          />
        }
      >
        <ConfirmDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          title={`Xoá đơn hàng ${order.orderId}?`}
          description="Hành động này không thể hoàn tác."
          onConfirm={handleDelete}
          isLoading={isDeleting}
          variant="destructive"
        />
        {/* ⚠️ CẢNH BÁO THIẾU THÔNG TIN PHÁP LÝ KHÁCH HÀNG */}
        {hasCustomerMissingInfo && ["DRAFT", "PENDING"].includes(order.status) && (
          <div className="mb-3 p-3 bg-amber-50/90 border border-amber-300 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-amber-900 shadow-xs">
            <div className="flex items-center gap-2.5 min-w-0">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
              <div className="text-xs sm:text-sm">
                <span className="font-semibold text-amber-950">Chưa đủ điều kiện duyệt đơn:</span> Khách hàng{" "}
                <span className="font-medium underline">{order.customer?.shortName || order.customer?.name}</span> còn thiếu{" "}
                <span className="font-bold text-rose-700">{missingCustomerFields.join(" và ")}</span>. Bắt buộc cập nhật đầy đủ trước khi duyệt đơn hàng.
              </div>
            </div>
            {order.customerId && (
              <Button
                size="sm"
                variant="outline"
                className="border-amber-400 bg-white hover:bg-amber-100 text-amber-900 text-xs font-semibold shrink-0 h-7 px-3 shadow-2xs"
                onClick={() =>
                  navigate({
                    to: `/customers/${order.customerId}` as any,
                    search: { returnUrl: `/orders/${orderId}` } as any,
                  })
                }
              >
                Cập nhật Khách Hàng
              </Button>
            )}
          </div>
        )}

        {/* ═══ 1. THÔNG TIN CHUNG (Dense Layout) ═══ */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1.5">
            <div className="col-span-2 md:col-span-2">
              <F
                label="Khách hàng"
                value={
                  order.customerId ? (
                    <EntityLink
                      entityType="customer"
                      id={order.customerId}
                      label={order.customer?.shortName || order.customer?.name}
                      search={{ returnUrl: `/orders/${orderId}` }}
                    />
                  ) : (
                    order.customer?.shortName || order.customer?.name || "—"
                  )
                }
                hl
              />
            </div>
            <F label="Ngày đặt" value={fmtD(order.orderDate)} />
            <F label="Hạn giao hàng" value={fmtD(order.deliveryDeadline)} />
            {/* FB-85B7J9: PO khách lưu theo từng dòng sản phẩm — đầu đơn gom các số PO khác nhau của các dòng */}
            <F
              label="PO khách"
              value={
                Array.from(
                  new Set(
                    (order.orderDetails || [])
                      .map((d: any) => String(d.poReference || "").trim())
                      .filter(Boolean),
                  ),
                ).join(", ") || null
              }
            />

            <div className="col-span-2 md:col-span-2">
              <F label="Địa chỉ giao" value={order.deliveryAddress} />
            </div>
            <F
              label="NV Sale"
              value={
                order.salesUser?.id ? (
                  <EntityLink entityType="user" id={order.salesUser.userId || order.salesUser.id} label={order.salesUser.fullName} />
                ) : (
                  <UserDisplay name={order.salesUser?.fullName} avatar={order.salesUser?.avatar} />
                )
              }
            />
            <F
              label="Ngoại tệ"
              value={
                <ConfigLabel
                  categoryCode="currencies"
                  value={order.currency}
                  fallback={order.currency}
                  className="bg-transparent border-0 p-0 text-sm font-semibold text-slate-800 shadow-none whitespace-normal text-left h-auto"
                />
              }
            />
            <F
              label="Tỷ giá"
              value={
                order.currency === "VND" ? (
                  <span className="text-slate-500">Không áp dụng (đơn VND)</span>
                ) : (
                  <span className="text-slate-600">
                    1{" "}
                    <span className="text-blue-700 font-bold">
                      {order.currency}
                    </span>{" "}
                    = {fmt(order.exchangeRate)} VND
                  </span>
                )
              }
            />
            <F
              label="Thanh toán"
              value={
                <ConfigLabel
                  categoryCode="payment_terms"
                  value={order.paymentTerm}
                  fallback={order.paymentTerm}
                  className="bg-transparent border-0 p-0 text-sm font-semibold text-slate-800 shadow-none whitespace-normal text-left h-auto"
                />
              }
            />
          </div>
          {(order.deposit ||
            order.quotationId ||
            order.deliveryMethod ||
            order.notes) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1.5 mt-1.5 pt-1.5 border-t border-slate-100">
              {order.deliveryMethod && (
                <F
                  label="HT giao hàng"
                  value={
                    <ConfigLabel
                      categoryCode="delivery_methods"
                      value={order.deliveryMethod}
                      fallback={order.deliveryMethod}
                      className="bg-transparent border-0 p-0 text-sm font-semibold text-slate-800 shadow-none whitespace-normal text-left h-auto"
                    />
                  }
                />
              )}
              {order.deposit && (
                <F
                  label="Đặt cọc"
                  value={
                    <ConfigLabel
                      categoryCode="deposit_options"
                      value={order.deposit}
                      fallback={order.deposit}
                      className="bg-transparent border-0 p-0 text-sm font-semibold text-slate-800 shadow-none whitespace-normal text-left h-auto"
                    />
                  }
                />
              )}
              {order.quotationId && (
                <F
                  label="Từ báo giá"
                  value={
                    <EntityLink
                      entityType="quotation"
                      id={order.quotationId}
                      code={order.quotation?.quotationId || order.quotationId}
                      search={{ returnUrl: `/orders/${orderId}` }}
                    />
                  }
                  mono
                />
              )}
              {order.notes && (
                <div className="col-span-2 md:col-span-4 pt-1.5 mt-1.5 border-t border-slate-100">
                  <F label="Ghi chú" value={order.notes} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══ 2. TABS: Sản phẩm | Giao hàng | Lệnh SX | Thanh toán | Lịch sử ═══ */}
        <RelatedDataTabs
          defaultTab="details"
          tabs={[
            {
              id: "details",
              label: "Sản phẩm",
              icon: <Package className="h-4 w-4" />,
              count: details.length,
              content: (
                <div className="p-0 border-t border-slate-100">
                  {details.length === 0 ? (
                    <div className="text-center text-sm text-slate-600 py-6">
                      Chưa có chi tiết sản phẩm
                    </div>
                  ) : (
                    (() => {
                      const normalized = details.map((d: any) => ({
                        ...d,
                        _sortDesc: (d.tags || d.product?.tags || "")
                          .toLowerCase()
                          .trim(),
                        // Sản phẩm khác mã nhưng trùng tên không được gộp chung ô (FB-9BRTHU)
                        _sortProduct: d.productId || d.product?.id || d.productCode || d.product?.productCode || '',
                        _sortName: (d.productName || d.productCode || "")
                          .toLowerCase()
                          .trim(),
                        _sortSpec: formatSpecs(d.specs, d.product),
                        // Nội dung ô "Chi tiết" (chất liệu + công đoạn): chỉ gộp ô các dòng liền nhau có chi tiết giống hệt.
                        // Trước đây gộp theo tags — dòng đơn không có tags nên mọi dòng liền nhau bị gộp, chỉ hiện chi tiết dòng đầu.
                        _detail: cleanProcessText(orderLineDetailText(d), formatSpecs(d.specs ?? d.product?.specs, d.product)),
                      }));

                      const sorted = normalized.sort((a: any, b: any) => {
                        if (a._sortDesc !== b._sortDesc)
                          return a._sortDesc.localeCompare(b._sortDesc);
                        if (a._sortName !== b._sortName)
                          return a._sortName.localeCompare(b._sortName);
                        return a._sortSpec.localeCompare(b._sortSpec);
                      });

                      const spanDetails: number[] = new Array(
                        sorted.length,
                      ).fill(0);
                      let gi = 0;
                      while (gi < sorted.length) {
                        let gj = gi + 1;
                        while (
                          gj < sorted.length &&
                          sorted[gj]._sortDesc === sorted[gi]._sortDesc &&
                          sorted[gj]._detail === sorted[gi]._detail
                        )
                          gj++;
                        spanDetails[gi] = gj - gi;
                        gi = gj;
                      }

                      const spanProduct: number[] = new Array(
                        sorted.length,
                      ).fill(0);
                      gi = 0;
                      while (gi < sorted.length) {
                        let gj = gi + 1;
                        while (
                          gj < sorted.length &&
                          sorted[gj]._sortDesc === sorted[gi]._sortDesc &&
                          sorted[gj]._sortName === sorted[gi]._sortName &&
                          sorted[gj]._sortProduct === sorted[gi]._sortProduct &&
                          sorted[gj]._sortSpec === sorted[gi]._sortSpec &&
                          sorted[gj]._detail === sorted[gi]._detail
                        )
                          gj++;
                        spanProduct[gi] = gj - gi;
                        gi = gj;
                      }

                      return (
                        <>
                          <div className="flex justify-between items-center px-4 py-3 bg-slate-50/50 border-b border-slate-100 rounded-t-lg">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-semibold text-slate-700">
                                Chi tiết sản phẩm ({details.length})
                              </span>
                              {selectedDetailIds.length > 0 && (
                                <Badge
                                  variant="secondary"
                                  className="text-indigo-700 bg-indigo-50 border-indigo-200"
                                >
                                  Đã chọn {selectedDetailIds.length}
                                </Badge>
                              )}
                              <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-xs">
                                <button
                                  type="button"
                                  onClick={() => setGroupByLayout(false)}
                                  className={`px-2 py-0.5 rounded-md font-medium transition-all ${
                                    !groupByLayout
                                      ? "bg-white text-slate-800 shadow-xs font-semibold"
                                      : "text-slate-500 hover:text-slate-800"
                                  }`}
                                >
                                  Bảng phẳng
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setGroupByLayout(true)}
                                  className={`px-2 py-0.5 rounded-md font-medium transition-all flex items-center gap-1 ${
                                    groupByLayout
                                      ? "bg-indigo-600 text-white shadow-xs font-semibold"
                                      : "text-slate-500 hover:text-slate-800"
                                  }`}
                                >
                                  <Layers className="w-3 h-3" />
                                  Gom theo Bài bình
                                </button>
                              </div>
                            </div>
                            {selectedDetailIds.length > 0 && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-amber-800 border-amber-300 hover:bg-amber-50"
                                    >
                                      <Layers className="mr-1.5 h-3.5 w-3.5 text-amber-600" /> Trạng Thái Bình File ({selectedDetailIds.length})
                                      <ChevronDown className="ml-1 h-3 w-3 opacity-60" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-56 text-xs">
                                    <DropdownMenuLabel className="text-[11px] text-slate-500 font-semibold px-2 py-1">
                                      Chuyển trạng thái hàng loạt
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => handleBulkUpdateDetailStatus(selectedDetailIds, "READY_FOR_PREPRESS")}
                                      className="text-amber-800 focus:bg-amber-50 cursor-pointer"
                                    >
                                      <span className="w-2 h-2 rounded-full bg-amber-500 mr-2" />
                                      Chuyển sang "Chờ bình file"
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleBulkConfirmPrepress(selectedDetailIds)}
                                      className="text-emerald-800 focus:bg-emerald-50 cursor-pointer"
                                    >
                                      <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                                      Xác nhận "Chờ tạo lệnh" (Đã bình)
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-purple-800 border-purple-300 hover:bg-purple-50 font-semibold"
                                  onClick={() => {
                                    const selectedItems = details.filter((d: any) =>
                                      selectedDetailIds.includes(d.id),
                                    );
                                    if (selectedItems.length > 0) {
                                      setImpositionDetail(selectedItems[0]);
                                      setShowImpositionModal(true);
                                    }
                                  }}
                                >
                                  <Layers className="mr-1.5 h-3.5 w-3.5 text-purple-600" /> Bình File Ghép Bài ({selectedDetailIds.length})
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-teal-600 hover:bg-teal-700 text-white"
                                  onClick={() => {
                                    navigate({
                                      to: "/delivery/create",
                                      search: {
                                        orderId: order.id,
                                        customerId: order.customerId,
                                        detailIds: selectedDetailIds.join(","),
                                      } as any,
                                    });
                                  }}
                                >
                                  <Truck className="mr-1.5 h-3.5 w-3.5" /> Giao Hàng Mục Đã Chọn ({selectedDetailIds.length})
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-rose-700 border-rose-300 hover:bg-rose-50"
                                  onClick={() => {
                                    const selectedItems = details.filter((d: any) =>
                                      selectedDetailIds.includes(d.id),
                                    );
                                    setRePrepressTargetItems(selectedItems);
                                    setRePrepressModalOpen(true);
                                  }}
                                >
                                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Yêu Cầu Bình Lại ({selectedDetailIds.length})
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                  onClick={() => {
                                    const selectedItems = details.filter((d: any) =>
                                      selectedDetailIds.includes(d.id),
                                    );
                                    const rePrepressSelected = selectedItems.filter(
                                      (d: any) => d.status === "RE_PREPRESS",
                                    );
                                    if (rePrepressSelected.length > 0) {
                                      toast.error(
                                        `Có ${rePrepressSelected.length} chi tiết đã chọn đang ở trạng thái 'Yêu cầu bình lại' (RE_PREPRESS). Chế bản cần xử lý xong trước khi tạo lệnh!`,
                                      );
                                      return;
                                    }
                                    const unassigned = selectedItems.filter(
                                      (d: any) => !d.prepressCode,
                                    );
                                    if (unassigned.length > 0) {
                                      toast.error(
                                        `Có ${unassigned.length} chi tiết đã chọn chưa hoàn tất thông số bình file. Bắt buộc phải hoàn tất bình file trước khi tạo Lệnh sản xuất!`,
                                      );
                                      return;
                                    }
                                    navigate({
                                      to: "/production-orders/create",
                                      search: {
                                        salesOrderId: order.id,
                                        detailIds: selectedDetailIds.join(","),
                                      } as any,
                                    });
                                  }}
                                >
                                  <Factory className="mr-1.5 h-3.5 w-3.5" /> Tạo
                                  LSX Gộp
                                </Button>
                              </div>
                            )}
                          </div>

                          {groupByLayout ? (
                            <div className="p-4 space-y-4">
                              {(() => {
                                const groupsMap = new Map<string, any[]>();
                                const unassigned: any[] = [];
                                for (const d of details) {
                                  if (d.prepressCode) {
                                    const list = groupsMap.get(d.prepressCode) || [];
                                    list.push(d);
                                    groupsMap.set(d.prepressCode, list);
                                  } else {
                                    unassigned.push(d);
                                  }
                                }
                                const groupEntries = Array.from(groupsMap.entries());

                                if (groupEntries.length === 0 && unassigned.length === 0) {
                                  return (
                                    <div className="text-center text-sm text-slate-500 py-8">
                                      Chưa có chi tiết sản phẩm nào
                                    </div>
                                  );
                                }

                                return (
                                  <>
                                    {groupEntries.map(([code, gItems]) => {
                                      const first = gItems[0];
                                      const paperSize = first?.prepressPaperSize || "Chưa rõ";
                                      const printSheets = first?.prepressPrintSheets || 0;
                                      const totalGroupUps = gItems.reduce(
                                        (sum: number, it: any) => sum + (Number(it.prepressUps) || 1),
                                        0
                                      );

                                      return (
                                        <div
                                          key={code}
                                          className="rounded-xl border border-indigo-200 bg-white overflow-hidden shadow-xs"
                                        >
                                          {/* Layout Card Header */}
                                          <div className="px-4 py-3 bg-gradient-to-r from-indigo-50/80 via-white to-sky-50/50 border-b border-indigo-100 flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex items-center gap-2.5">
                                              <div className="p-1.5 rounded-lg bg-indigo-600 text-white">
                                                <Layers className="w-4 h-4" />
                                              </div>
                                              <div>
                                                <div className="flex items-center gap-2">
                                                  <span className="font-mono font-bold text-sm text-indigo-950">
                                                    {code}
                                                  </span>
                                                  <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 text-[11px] font-mono">
                                                    {gItems.length} Sản phẩm
                                                  </Badge>
                                                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[11px] font-mono">
                                                    {totalGroupUps} UPS Tổng
                                                  </Badge>
                                                </div>
                                                <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                                                  <span>
                                                    Khổ in: <b className="text-slate-800 font-mono">{paperSize}</b>
                                                  </span>
                                                  <span>•</span>
                                                  <span>
                                                    Tờ in máy chạy:{" "}
                                                    <b className="text-amber-700 font-mono">
                                                      {Number(printSheets).toLocaleString()}
                                                    </b>{" "}
                                                    tờ
                                                  </span>
                                                </div>
                                              </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-8 text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50 flex items-center gap-1.5"
                                                onClick={() => {
                                                  setImpositionDetail(first);
                                                  setSelectedDetailIds(gItems.map((it: any) => it.id));
                                                  setShowImpositionModal(true);
                                                }}
                                              >
                                                <Layers className="w-3.5 h-3.5" /> Mở Studio Bình Lại
                                              </Button>
                                              <Button
                                                size="sm"
                                                className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1.5 shadow-xs"
                                                // Chỉ dòng còn SL cần lên lệnh (cùng quy tắc danh sách chờ lên lệnh); hết thì khoá nút
                                                disabled={openItemsOf(gItems).length === 0}
                                                title={openItemsOf(gItems).length === 0 ? "Các dòng trong bài bình này đã lên LSX đủ số lượng" : undefined}
                                                onClick={() => {
                                                  navigate({
                                                    to: "/production-orders/create",
                                                    search: {
                                                      salesOrderId: order.id,
                                                      detailIds: openItemsOf(gItems).map((it: any) => it.id).join(","),
                                                      layoutCode: code !== "__UNASSIGNED__" ? code : undefined,
                                                      layoutId: first?.printLayoutId || undefined,
                                                    } as any,
                                                  });
                                                }}
                                              >
                                                <Factory className="w-3.5 h-3.5" /> {openItemsOf(gItems).length === 0 ? "Đã lên LSX đủ" : "Lên LSX Ghép Bài"}
                                              </Button>
                                            </div>
                                          </div>

                                          {/* Layout Items Table */}
                                          <div className="overflow-x-auto">
                                            <table className="w-full text-xs text-left border-collapse">
                                              <thead>
                                                <tr className="bg-slate-50 border-b border-slate-100 text-slate-500 font-semibold text-[11px]">
                                                  <th className="py-2 px-3">Sản phẩm</th>
                                                  <th className="py-2 px-3 text-right">SL Đặt</th>
                                                  <th className="py-2 px-3 text-center">UPS Con</th>
                                                  <th className="py-2 px-3 text-right">SL Kế Hoạch</th>
                                                  <th className="py-2 px-3 text-center">Trạng Thái</th>
                                                  <th className="py-2 px-3 text-right">Thao Tác</th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-slate-100">
                                                {gItems.map((it: any) => (
                                                  <tr key={it.id} className="hover:bg-slate-50/60">
                                                    <td className="py-2.5 px-3">
                                                      <span className="font-semibold text-slate-800">{it.productName}</span>
                                                      <div className="text-[10px] text-slate-400 font-mono">
                                                        {it.productCode || it.detailId}
                                                      </div>
                                                    </td>
                                                    <td className="py-2.5 px-3 text-right font-mono font-medium text-slate-700">
                                                      {Number(it.quantity).toLocaleString()}
                                                    </td>
                                                    <td className="py-2.5 px-3 text-center font-mono">
                                                      <Badge className="bg-slate-100 text-slate-800 border-slate-200 font-bold">
                                                        {it.prepressUps || 1} UP
                                                      </Badge>
                                                    </td>
                                                    <td className="py-2.5 px-3 text-right font-mono text-slate-600">
                                                      {Number(it.remainingPlanQty ?? it.quantity).toLocaleString()}
                                                    </td>
                                                    <td className="py-2.5 px-3 text-center">
                                                      <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-[10px]">
                                                        {it.status || "PREPRESS_PLANNED"}
                                                      </Badge>
                                                    </td>
                                                    <td className="py-2.5 px-3 text-right">
                                                      <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-7 text-xs text-slate-600 hover:text-indigo-600"
                                                        onClick={() => handleOpenImposition(it)}
                                                      >
                                                        Sửa
                                                      </Button>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      );
                                    })}

                                    {/* Unassigned Items Card */}
                                    {unassigned.length > 0 && (
                                      <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/40 p-4 space-y-3">
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4 text-amber-600" />
                                            <span className="font-bold text-sm text-amber-900">
                                              Sản phẩm chưa gán bài bình ({unassigned.length})
                                            </span>
                                          </div>
                                          <Button
                                            size="sm"
                                            className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white font-medium"
                                            onClick={() => {
                                              setImpositionDetail(unassigned[0]);
                                              setSelectedDetailIds(unassigned.map((it: any) => it.id));
                                              setShowImpositionModal(true);
                                            }}
                                          >
                                            <Layers className="w-3.5 h-3.5 mr-1.5" /> Ghép tất cả vào Bài bình mới
                                          </Button>
                                        </div>

                                        <div className="bg-white rounded-lg border border-amber-200 overflow-x-auto">
                                          <table className="w-full text-xs text-left">
                                            <thead>
                                              <tr className="bg-amber-100/50 text-amber-900 border-b border-amber-200 text-[11px] font-semibold">
                                                <th className="py-2 px-3">Tên sản phẩm</th>
                                                <th className="py-2 px-3 text-right">Số lượng</th>
                                                <th className="py-2 px-3 text-center">Khổ trải</th>
                                                <th className="py-2 px-3 text-right">Hành động</th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-amber-100">
                                              {unassigned.map((it: any) => (
                                                <tr key={it.id} className="hover:bg-amber-50/30">
                                                  <td className="py-2 px-3 font-medium text-slate-800">{it.productName}</td>
                                                  <td className="py-2 px-3 text-right font-mono">
                                                    {Number(it.quantity).toLocaleString()}
                                                  </td>
                                                  <td className="py-2 px-3 text-center font-mono text-slate-500">
                                                    {it.product?.flatLength && it.product?.flatWidth
                                                      ? `${it.product.flatLength}x${it.product.flatWidth}mm`
                                                      : "—"}
                                                  </td>
                                                  <td className="py-2 px-3 text-right">
                                                    <Button
                                                      size="sm"
                                                      variant="ghost"
                                                      className="h-7 text-xs text-amber-800 hover:bg-amber-100"
                                                      onClick={() => handleOpenImposition(it)}
                                                    >
                                                      Bình file
                                                    </Button>
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          ) : (
                            <>
                              <div className="md:hidden space-y-3 p-3 bg-slate-50/50">
                            {sorted.map((d: any, idx: number) => {
                              const dqty = Number(d.qtyDelivered || 0);
                              const oqty = Number(d.quantity || 0);
                              const pct =
                                oqty > 0 ? Math.round((dqty / oqty) * 100) : 0;
                              const remain = oqty - dqty;

                              return (
                                <div
                                  key={d.id || idx}
                                  className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
                                >
                                  <div className="p-3 border-b border-slate-100 flex gap-3 items-start relative">
                                    <div className="w-12 h-12 rounded border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0 overflow-hidden">
                                      {d.product?.image ? (
                                        <ImageLightbox src={d.product.image} alt={d.productName || "HÌNH ẢNH SP"}>
                                          <img
                                            src={d.product.image}
                                            className="w-full h-full object-contain cursor-pointer hover:opacity-80 transition-opacity"
                                            alt=""
                                          />
                                        </ImageLightbox>
                                      ) : (
                                        <Package className="w-5 h-5 text-slate-300" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0 pr-8">
                                      <div className="flex justify-between items-start">
                                        {d.productId ? (
                                          <EntityLink
                                            entityType="product"
                                            id={d.productId}
                                            label={d.productName || "—"}
                                            search={{ returnUrl: `/orders/${orderId}` }}
                                            className="font-bold text-[13px] break-words"
                                          />
                                        ) : (
                                          <div
                                            className="font-bold text-[13px] text-slate-900 break-words"
                                            data-testid="order-item-product-name-mobile"
                                          >
                                            {d.productName || "—"}
                                          </div>
                                        )}
                                      </div>
                                      <div className="text-[11px] text-slate-500 break-words whitespace-normal mt-0.5 flex items-center gap-1.5 flex-wrap">
                                        <span>
                                          {d.productCode ||
                                            d.product?.productCode}
                                        </span>
                                        {(() => {
                                          const rawS = d.specs ?? d.product?.specs ?? {};
                                          const parsedS =
                                            typeof rawS === "string"
                                              ? (() => {
                                                  try {
                                                    return JSON.parse(rawS);
                                                  } catch {
                                                    return {};
                                                  }
                                                })()
                                              : rawS;
                                          const fsc =
                                            parsedS?.fscCode && parsedS.fscCode !== "NONE"
                                              ? String(parsedS.fscCode)
                                              : null;
                                          return fsc ? (
                                            <Badge
                                              variant="outline"
                                              className="text-[9px] px-1.5 py-0 h-auto min-h-4 bg-emerald-50 text-emerald-700 border-emerald-300 font-semibold whitespace-normal text-left break-words max-w-full shrink rounded-md"
                                              data-testid="order-item-fsc-badge-mobile"
                                            >
                                              🌲 {fsc}
                                            </Badge>
                                          ) : null;
                                        })()}
                                      </div>
                                      {(d.poReference || d.soReference) && (
                                        <div className="text-[10px] text-slate-400 mt-1 font-mono whitespace-pre-wrap">
                                          PO: {d.poReference || "-"} | SO:{" "}
                                          {d.soReference || "-"}
                                        </div>
                                      )}
                                      {d.notes && (
                                        <div className="text-[11px] text-amber-800 mt-1 whitespace-pre-wrap">Ghi chú: {d.notes}</div>
                                      )}
                                    </div>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          className="h-8 w-8 p-0 absolute top-2 right-2"
                                        >
                                          <MoreHorizontal className="h-4 w-4 text-slate-400" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                          onSelect={() => handleOpenImposition(d)}
                                        >
                                          <Layers className="mr-2 h-4 w-4 text-purple-600" />
                                          <span className="text-purple-700 font-medium">
                                            {d.prepressCode ? "Sửa bài bình (Prepress)" : "Bình file (Prepress)"}
                                          </span>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onSelect={() => {
                                            if (d.productId) {
                                              navigate({
                                                to: "/products/$productId",
                                                params: { productId: d.productId },
                                                search: { returnUrl: `/orders/${orderId}` },
                                              });
                                            }
                                          }}
                                        >
                                          <Eye className="mr-2 h-4 w-4" /> Xem
                                          sản phẩm
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onSelect={() =>
                                            navigate({
                                              to: "/orders/$orderId/edit",
                                              params: { orderId },
                                            })
                                          }
                                        >
                                          <Pencil className="mr-2 h-4 w-4" />{" "}
                                          Sửa chi tiết ĐH
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>

                                  <div className="p-3 grid grid-cols-2 gap-y-3 gap-x-4 bg-slate-50/30 text-xs">
                                    <div>
                                      <div className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">
                                        SL Đặt
                                      </div>
                                      <div className="font-bold text-slate-900 flex items-center gap-1">
                                        <span className="text-sm">
                                          {formatNumber(d.quantity)}
                                        </span>
                                        <Badge
                                          variant="outline"
                                          className="text-[9px] px-1 py-0 h-4"
                                        >
                                          {d.unit || "Cái"}
                                        </Badge>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">
                                        Thành tiền
                                      </div>
                                      <div className="font-bold text-indigo-700 text-sm">
                                        {fmt(
                                          Number(d.amount) ||
                                            (Number(d.quantity) || 0) *
                                              (Number(d.unitPrice) ||
                                                Number(d.priceVND) ||
                                                0),
                                        )}
                                      </div>
                                      <div className="text-[10px] text-slate-400 mt-0.5">
                                        Đơn giá: {fmt(d.unitPrice)}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">
                                        SL Sản xuất
                                      </div>
                                      <div className="font-bold text-blue-600">
                                        {formatNumber(d.lsxQty || d.quantity)}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-slate-500 text-[11px] uppercase tracking-wider mb-1">
                                        Đã giao
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span
                                          className={cn(
                                            "font-bold",
                                            pct >= 100
                                              ? "text-emerald-600"
                                              : "text-amber-600",
                                          )}
                                        >
                                          {formatNumber(dqty)}
                                        </span>
                                        {remain > 0 ? (
                                          <span className="text-[10px] text-rose-600 font-medium">
                                            Thiếu {formatNumber(remain)}
                                          </span>
                                        ) : (
                                          <span className="text-[10px] text-emerald-600">
                                            Đủ
                                          </span>
                                        )}
                                      </div>
                                      <div className="w-full h-1 bg-slate-200 rounded-full mt-1.5 overflow-hidden">
                                        <div
                                          className={cn(
                                            "h-full rounded-full",
                                            pct >= 100
                                              ? "bg-emerald-500"
                                              : "bg-amber-400",
                                          )}
                                          style={{
                                            width: `${Math.min(pct, 100)}%`,
                                          }}
                                        />
                                    {/* Prepress specifications in mobile card */}
                                    <div className="col-span-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-[11px] text-slate-500 font-semibold">Bình file:</span>
                                        {d.prepressCode ? (
                                          <span className="text-xs font-mono font-bold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-200">
                                            {d.prepressCode} ({d.prepressUps} UP - {formatNumber(d.prepressPrintSheets)} tờ)
                                          </span>
                                        ) : (
                                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-[10px]">
                                            Chờ bình file
                                          </Badge>
                                        )}
                                      </div>
                                      {!d.lsxId && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-6 text-[10px] px-2 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenImposition(d);
                                          }}
                                        >
                                          <Layers className="w-3 h-3 mr-1" />
                                          {d.prepressCode ? "Sửa" : "Bình file"}
                                        </Button>
                                      )}
                                    </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}

                            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 mt-4">
                              <div className="flex justify-between items-center text-xs text-slate-600 mb-1">
                                <span>Tổng số lượng:</span>
                                <span className="font-bold text-slate-900">
                                  {formatNumber(
                                    sorted.reduce(
                                      (s: number, d: any) =>
                                        s + Number(d.quantity || 0),
                                      0,
                                    ),
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between items-center text-xs text-slate-600 mb-1">
                                <span>Tổng thành tiền (Subtotal):</span>
                                <span className="font-bold text-slate-900">
                                  {fmt(lineTotal)}
                                </span>
                              </div>
                              {vatAmount > 0 && (
                                <div className="flex justify-between items-center text-xs text-slate-600 mb-1">
                                  <span>VAT ({Number(order.vatRate)}%):</span>
                                  <span className="font-bold text-slate-900">
                                    {fmt(vatAmount)}
                                  </span>
                                </div>
                              )}
                              {shippingFee > 0 && (
                                <div className="flex justify-between items-center text-xs text-slate-600 mb-1">
                                  <span>Chi phí vận chuyển:</span>
                                  <span className="font-bold text-slate-900">{fmt(shippingFee)}</span>
                                </div>
                              )}
                              <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                                <span className="text-sm font-bold text-slate-900 uppercase">
                                  Tổng cộng (Grand Total):
                                </span>
                                <span className="text-lg font-black text-indigo-600">
                                  {fmt(totalAmount)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="hidden md:block overflow-x-auto">
                            <Table className="table-fixed min-w-[2180px]">
                              <TableHeader>
                                <TableRow>
                                  <TableHead
                                    style={{ width: "40px" }}
                                    className="text-center px-2"
                                  >
                                    <Checkbox
                                      checked={
                                        sorted.length > 0 &&
                                        selectedDetailIds.length === sorted.length
                                      }
                                      onCheckedChange={(checked) => {
                                        if (checked) {
                                          setSelectedDetailIds(sorted.map((d: any) => d.id));
                                        } else {
                                          setSelectedDetailIds([]);
                                        }
                                      }}
                                    />
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "45px" }}
                                    className="text-center"
                                  >
                                    STT
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "230px" }}
                                    className="whitespace-nowrap"
                                  >
                                    Sản phẩm
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "210px" }}
                                    className="whitespace-nowrap"
                                  >
                                    Chi tiết
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "120px" }}
                                    className="whitespace-nowrap"
                                  >
                                    PO/SO
                                  </TableHead>

                                  <TableHead
                                    style={{ width: "60px" }}
                                    className="whitespace-nowrap text-center"
                                  >
                                    ĐVT
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "85px" }}
                                    className="text-right whitespace-nowrap"
                                  >
                                    SL đặt
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "85px" }}
                                    className="text-right whitespace-nowrap text-amber-600 font-bold"
                                    title="Số lượng tồn kho"
                                  >
                                    SL Tồn
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "85px" }}
                                    className="text-right whitespace-nowrap text-slate-500"
                                    title="Số lượng bù hao"
                                  >
                                    SL bù
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "85px" }}
                                    className="text-right whitespace-nowrap text-blue-600 font-bold"
                                    title="Số lượng sản xuất"
                                  >
                                    SL SX
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "100px" }}
                                    className="text-right whitespace-nowrap px-2"
                                  >
                                    Đơn giá
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "120px" }}
                                    className="text-right whitespace-nowrap px-2"
                                  >
                                    Thành tiền
                                  </TableHead>

                                  <TableHead
                                    style={{ width: "85px" }}
                                    className="text-right whitespace-nowrap px-2 text-emerald-700"
                                  >
                                    Đã giao
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "80px" }}
                                    className="text-right whitespace-nowrap px-2 text-rose-600"
                                  >
                                    Thiếu
                                  </TableHead>
                                  {/* Nhóm thông số bài bình */}
                                  <TableHead
                                    style={{ width: "135px" }}
                                    className="whitespace-nowrap text-indigo-700 font-bold px-2"
                                  >
                                    Mã bài bình
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "105px" }}
                                    className="whitespace-nowrap px-2"
                                  >
                                    Khổ in
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "70px" }}
                                    className="text-center whitespace-nowrap px-2"
                                  >
                                    Ups
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "95px" }}
                                    className="text-right whitespace-nowrap px-2 text-emerald-700 font-bold"
                                  >
                                    Tờ in
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "140px" }}
                                    className="whitespace-nowrap px-2"
                                  >
                                    Ghi chú KT
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "110px" }}
                                    className="text-center whitespace-nowrap"
                                  >
                                    Trạng thái
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "105px" }}
                                    className="whitespace-nowrap"
                                  >
                                    Hạn giao
                                  </TableHead>
                                  <TableHead
                                    style={{ width: "60px" }}
                                    className="text-right whitespace-nowrap"
                                  >
                                    Thao tác
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {sorted.map((d: any, idx: number) => {
                                  const specsText = formatSpecs(
                                    d.specs ?? d.product?.specs,
                                    d.product,
                                  );
                                  // Chất liệu trước, rồi các công đoạn (VD "Ivory 300gsm, In Offset, Tráng phủ...")
                                  const processText: string = d._detail;

                                  const showMergeProduct = spanProduct[idx] > 0;
                                  const showMergeDetails = spanDetails[idx] > 0;

                                  return (
                                    <TableRow
                                      key={d.id || idx}
                                      className="group cursor-pointer hover:bg-slate-50"
                                      onClick={async () => {
                                        if (selectedDetailIds.includes(d.id)) {
                                          setSelectedDetailIds((prev) =>
                                            prev.filter((id) => id !== d.id),
                                          );
                                        } else {
                                          setSelectedDetailIds((prev) => [
                                            ...prev,
                                            d.id,
                                          ]);
                                        }
                                      }}
                                    >
                                      <TableCell className="text-center px-2">
                                        <Checkbox
                                          checked={selectedDetailIds.includes(
                                            d.id,
                                          )}
                                          onCheckedChange={(checked) => {
                                            if (checked) {
                                              setSelectedDetailIds((prev) => [
                                                ...prev,
                                                d.id,
                                              ]);
                                            } else {
                                              setSelectedDetailIds((prev) =>
                                                prev.filter(
                                                  (id) => id !== d.id,
                                                ),
                                              );
                                            }
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </TableCell>
                                      <TableCell className="text-center text-slate-600">
                                        {idx + 1}
                                      </TableCell>

                                      {showMergeProduct && (
                                        <TableCell
                                          rowSpan={spanProduct[idx]}
                                          className="align-middle overflow-hidden"
                                        >
                                          <div className="flex items-center gap-2.5">
                                            {d.product?.image ? (
                                              <ImageLightbox src={d.product.image} alt={d.productName || "HÌNH ẢNH SP"}>
                                                <img
                                                  src={d.product.image}
                                                  className="w-[34px] h-[34px] object-contain rounded border border-slate-200 shrink-0 cursor-pointer hover:opacity-80 transition-opacity bg-white"
                                                  alt="HÌNH ẢNH SP"
                                                />
                                              </ImageLightbox>
                                            ) : (
                                              <div className="w-[34px] h-[34px] rounded border border-slate-200 bg-white flex items-center justify-center shrink-0">
                                                <Package className="w-4 h-4 text-slate-300" />
                                              </div>
                                            )}
                                            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                              <div className="flex items-start gap-2 max-w-full">
                                                {d.productGroup && (
                                                  <Badge
                                                    variant="outline"
                                                    className="text-[10px] font-medium uppercase px-1.5 py-0 min-h-0 bg-slate-100 text-slate-700 whitespace-nowrap shrink-0 mt-0.5"
                                                  >
                                                    {d.productGroup}
                                                  </Badge>
                                                )}
                                                <div className="flex flex-col gap-0 w-full min-w-0">
                                                  <div className="mb-0.5 flex items-center gap-1.5 flex-wrap">
                                                    {(d.productCode ||
                                                      d.product?.productCode) && (
                                                      <span
                                                        className="font-mono text-[10px] bg-slate-100 px-1 rounded text-slate-600 border border-slate-200"
                                                        title={
                                                          d.productCode ||
                                                          d.product?.productCode
                                                        }
                                                      >
                                                        {d.productCode ||
                                                          d.product
                                                            ?.productCode}
                                                      </span>
                                                    )}
                                                    {(() => {
                                                      const parseObj = (val: unknown): Record<string, any> => {
                                                        if (!val) return {};
                                                        if (typeof val === "object") return val as Record<string, any>;
                                                        if (typeof val === "string") {
                                                          try {
                                                            const p = JSON.parse(val);
                                                            return p && typeof p === "object" ? p : {};
                                                          } catch {
                                                            return {};
                                                          }
                                                        }
                                                        return {};
                                                      };
                                                      const detailS = parseObj(d.specs);
                                                      const prodS = parseObj(d.product?.specs);
                                                      const fscCandidate = detailS?.fscCode || prodS?.fscCode;
                                                      const fsc =
                                                        fscCandidate && fscCandidate !== "NONE"
                                                          ? String(fscCandidate)
                                                          : null;
                                                      return fsc ? (
                                                        <Badge
                                                          variant="outline"
                                                          className="text-[9px] px-1.5 py-0 h-auto min-h-4 bg-emerald-50 text-emerald-700 border-emerald-300 font-semibold whitespace-normal text-left break-words max-w-full shrink rounded-md"
                                                          data-testid="order-item-fsc-badge"
                                                        >
                                                          🌲 {fsc}
                                                        </Badge>
                                                      ) : null;
                                                    })()}
                                                  </div>
                                                  {d.productId ? (
                                                    <EntityLink
                                                      entityType="product"
                                                      id={d.productId}
                                                      label={d.productName || "—"}
                                                      search={{ returnUrl: `/orders/${orderId}` }}
                                                      className="text-xs font-bold"
                                                      title={d.productName}
                                                    />
                                                  ) : (
                                                    <div
                                                      className="text-xs font-bold break-words text-slate-900"
                                                      title={d.productName}
                                                      data-testid="order-item-product-name"
                                                    >
                                                      {d.productName || "—"}
                                                    </div>
                                                  )}
                                                  {specsText && (
                                                    <div
                                                      className="text-[11px] text-slate-700 italic whitespace-normal break-words mt-0.5"
                                                      title={specsText}
                                                    >
                                                      {specsText}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </TableCell>
                                      )}

                                      {showMergeDetails && (
                                        <TableCell
                                          rowSpan={spanDetails[idx]}
                                          className="align-middle border-l border-slate-100"
                                          data-testid={`line-detail-${d.id}`}
                                        >
                                          {processText ? (
                                            <div className="text-xs text-slate-700 whitespace-pre-wrap">
                                              {processText}
                                            </div>
                                          ) : (
                                            <span className="text-slate-300">
                                              —
                                            </span>
                                          )}
                                        </TableCell>
                                      )}

                                      <TableCell className="align-middle border-l border-slate-100">
                                        <div className="flex flex-col gap-1 text-[11px] font-mono text-slate-700 min-w-[80px] max-w-[140px] whitespace-pre-wrap break-words">
                                          {d.poReference && (
                                            <div>
                                              <span className="text-[9px] text-slate-400 uppercase mr-1">
                                                PO:
                                              </span>
                                              <span className="break-all">
                                                {d.poReference}
                                              </span>
                                            </div>
                                          )}
                                          {d.soReference && (
                                            <div>
                                              <span className="text-[9px] text-slate-400 uppercase mr-1">
                                                SO:
                                              </span>
                                              <span className="break-all">
                                                {d.soReference}
                                              </span>
                                            </div>
                                          )}
                                          {!d.poReference && !d.soReference && !d.notes && (
                                            <span className="text-slate-300">
                                              —
                                            </span>
                                          )}
                                          {/* Ghi chú dòng (lưu trên dòng đơn hàng, in trên đơn) */}
                                          {d.notes && (
                                            <div className="font-sans text-amber-800" data-testid="order-line-note">
                                              Ghi chú: {d.notes}
                                            </div>
                                          )}
                                        </div>
                                      </TableCell>

                                      {showMergeProduct && (
                                        <TableCell
                                          rowSpan={spanProduct[idx]}
                                          className="text-xs align-middle px-2"
                                        >
                                          {d.unit || "Cái"}
                                        </TableCell>
                                      )}

                                      <TableCell className="text-right tabular-nums align-middle px-2">
                                        {formatNumber(d.quantity)}
                                      </TableCell>
                                      <TableCell className="text-right tabular-nums align-middle px-2 text-amber-600">
                                        {formatNumber(d.stockQty || 0)}
                                      </TableCell>
                                      <TableCell className="text-right tabular-nums align-middle px-2 text-slate-500">
                                        {formatNumber(
                                          Math.max(
                                            0,
                                            Math.ceil(
                                              d.quantity *
                                                (1 +
                                                  (d.allowancePercent || 0) /
                                                    100),
                                            ) - d.quantity,
                                          ),
                                        )}
                                      </TableCell>
                                      <TableCell className="text-right tabular-nums align-middle px-2 font-semibold text-blue-700">
                                        {formatNumber(d.lsxQty || d.quantity)}
                                      </TableCell>
                                      <TableCell className="text-right tabular-nums align-middle">
                                        {fmt(d.unitPrice)}
                                      </TableCell>
                                      <TableCell className="text-right tabular-nums font-semibold align-middle">
                                        {fmt(
                                          Number(d.amount) ||
                                            (Number(d.quantity) || 0) *
                                              (Number(d.unitPrice) ||
                                                Number(d.priceVND) ||
                                                0),
                                        )}
                                      </TableCell>

                                      {/* Đã giao */}
                                      <TableCell className="text-right align-middle">
                                        {(() => {
                                          const dqty = Number(
                                            d.qtyDelivered || 0,
                                          );
                                          const oqty = Number(d.quantity || 0);
                                          const pct =
                                            oqty > 0
                                              ? Math.round((dqty / oqty) * 100)
                                              : 0;
                                          return dqty > 0 ? (
                                            <div>
                                              <span
                                                className={cn(
                                                  "font-bold tabular-nums",
                                                  pct >= 100
                                                    ? "text-emerald-600"
                                                    : "text-amber-600",
                                                )}
                                              >
                                                {formatNumber(dqty)}
                                              </span>
                                              <div className="flex items-center gap-1 mt-0.5 justify-end">
                                                <div className="w-10 h-1 bg-slate-100 rounded-full overflow-hidden">
                                                  <div
                                                    className={cn(
                                                      "h-full rounded-full",
                                                      pct >= 100
                                                        ? "bg-emerald-500"
                                                        : "bg-amber-400",
                                                    )}
                                                    style={{
                                                      width: `${Math.min(pct, 100)}%`,
                                                    }}
                                                  />
                                                </div>
                                                <span className="text-[10px] text-slate-600">
                                                  {pct}%
                                                </span>
                                              </div>
                                            </div>
                                          ) : (
                                            <span className="text-slate-300">
                                              —
                                            </span>
                                          );
                                        })()}
                                      </TableCell>
                                      {/* Thiếu */}
                                      <TableCell className="text-right tabular-nums align-middle px-3">
                                        {(() => {
                                          const remain =
                                            Number(d.quantity || 0) -
                                            Number(d.qtyDelivered || 0);
                                          return remain > 0 ? (
                                            <span className="text-rose-600 font-bold">
                                              {formatNumber(remain)}
                                            </span>
                                          ) : (
                                            <span className="text-emerald-500">
                                              ✓
                                            </span>
                                          );
                                        })()}
                                      </TableCell>

                                      {/* Mã bài bình */}
                                      <TableCell className="align-middle border-l border-slate-100 px-2 font-mono text-xs">
                                        {d.prepressCode ? (
                                          <div className="flex items-center gap-1">
                                            <span
                                              className="font-bold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-200 break-words whitespace-normal max-w-[100px]"
                                              title={`${d.prepressCode}${d.prepressNotes ? " - " + d.prepressNotes : ""}`}
                                            >
                                              {d.prepressCode}
                                            </span>
                                            {!d.lsxId && d.status !== "IN_PRODUCTION" && d.status !== "COMPLETED" && (
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0 text-slate-400 hover:text-indigo-600 shrink-0"
                                                title="Sửa bài bình"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleOpenImposition(d);
                                                }}
                                              >
                                                <Pencil className="h-3 w-3" />
                                              </Button>
                                            )}
                                          </div>
                                        ) : d.lsxId || d.status === "IN_PRODUCTION" || d.status === "COMPLETED" || d.status === "DELIVERED" ? (
                                          <span className="text-slate-300 font-normal">—</span>
                                        ) : (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-6 text-[11px] px-2 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 font-semibold gap-1 shadow-none"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOpenImposition(d);
                                            }}
                                          >
                                            <Layers className="h-3 w-3 text-purple-600" />
                                            Bình file
                                          </Button>
                                        )}
                                      </TableCell>

                                      {/* Khổ in */}
                                      <TableCell className="align-middle px-2 text-xs text-slate-700 whitespace-nowrap">
                                        {d.prepressPaperSize || <span className="text-slate-300">—</span>}
                                      </TableCell>

                                      {/* Ups */}
                                      <TableCell className="align-middle px-2 text-center text-xs font-semibold tabular-nums">
                                        {d.prepressUps ? (
                                          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-slate-50 font-mono">
                                            {d.prepressUps} UP
                                          </Badge>
                                        ) : (
                                          <span className="text-slate-300">—</span>
                                        )}
                                      </TableCell>

                                      {/* Tờ in */}
                                      <TableCell className="align-middle px-2 text-right text-xs font-bold font-mono text-emerald-700 tabular-nums">
                                        {d.prepressPrintSheets ? (
                                          formatNumber(d.prepressPrintSheets)
                                        ) : (
                                          <span className="text-slate-300 font-normal">—</span>
                                        )}
                                      </TableCell>

                                      {/* Ghi chú kỹ thuật */}
                                      <TableCell className="align-middle px-2 text-xs text-slate-600 max-w-[140px]">
                                        {d.prepressNotes ? (
                                          <span className="break-words whitespace-normal block" title={d.prepressNotes}>
                                            {d.prepressNotes}
                                          </span>
                                        ) : (
                                          <span className="text-slate-300">—</span>
                                        )}
                                      </TableCell>

                                      {/* Trạng thái */}
                                      <TableCell className="text-center align-middle border-l border-slate-100 px-2" title={d.rePrepressReason ? `Lý do: ${d.rePrepressReason}` : undefined}>
                                        <div className="flex flex-col items-center gap-1">
                                          {d.lsxId || d.status === "IN_PRODUCTION" || d.status === "COMPLETED" || d.status === "DELIVERED" ? (
                                            <StatusBadge
                                              type="OrderDetailStatus"
                                              value={
                                                d.status && d.status !== "PENDING"
                                                  ? d.status
                                                  : d.lsxId
                                                    ? "PO_CREATED"
                                                    : d.prepressCode
                                                      ? "PREPRESS_PLANNED"
                                                      : "READY_FOR_PREPRESS"
                                              }
                                              showDot
                                              size="xs"
                                            />
                                          ) : (
                                            <DropdownMenu>
                                              <DropdownMenuTrigger asChild>
                                                <button
                                                  type="button"
                                                  className="cursor-pointer group inline-flex items-center gap-1 rounded-sm hover:opacity-85 focus:outline-hidden transition-opacity"
                                                  title="Nhấn để chuyển trạng thái nhanh"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <StatusBadge
                                                    type="OrderDetailStatus"
                                                    value={
                                                      d.status && d.status !== "PENDING"
                                                        ? d.status
                                                        : d.lsxId
                                                          ? "PO_CREATED"
                                                          : d.prepressCode
                                                            ? "PREPRESS_PLANNED"
                                                            : "READY_FOR_PREPRESS"
                                                    }
                                                    showDot
                                                    size="xs"
                                                  />
                                                  <ChevronDown className="h-3 w-3 text-slate-400 group-hover:text-slate-600 transition-colors" />
                                                </button>
                                              </DropdownMenuTrigger>
                                              <DropdownMenuContent align="center" className="w-52 text-xs">
                                                <DropdownMenuLabel className="text-[11px] text-slate-500 font-semibold px-2 py-1">
                                                  Đổi trạng thái chi tiết
                                                </DropdownMenuLabel>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                  onClick={() => handleUpdateDetailStatus(d.id, "READY_FOR_PREPRESS")}
                                                  className="text-amber-800 focus:bg-amber-50 cursor-pointer font-medium"
                                                >
                                                  <span className="w-2 h-2 rounded-full bg-amber-500 mr-2" />
                                                  Chờ bình file
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                  onClick={() => {
                                                    if (!d.prepressCode) {
                                                      toast.error("Chi tiết này chưa có thông số bài bình, vui lòng nhập thông số trước!");
                                                      handleOpenImposition(d);
                                                      return;
                                                    }
                                                    handleUpdateDetailStatus(d.id, "PREPRESS_PLANNED");
                                                  }}
                                                  className="text-emerald-800 focus:bg-emerald-50 cursor-pointer font-medium"
                                                >
                                                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                                                  Chờ tạo lệnh (Đã bình)
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                  onClick={() => {
                                                    setRePrepressTargetItems([d]);
                                                    setRePrepressModalOpen(true);
                                                  }}
                                                  className="text-rose-800 focus:bg-rose-50 cursor-pointer font-medium"
                                                >
                                                  <span className="w-2 h-2 rounded-full bg-rose-500 mr-2" />
                                                  Yêu cầu bình lại
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                  onClick={() => handleUpdateDetailStatus(d.id, "CANCELLED")}
                                                  className="text-slate-600 focus:bg-slate-100 cursor-pointer"
                                                >
                                                  <span className="w-2 h-2 rounded-full bg-slate-400 mr-2" />
                                                  Hủy dòng này
                                                </DropdownMenuItem>
                                              </DropdownMenuContent>
                                            </DropdownMenu>
                                          )}
                                          {d.status === "PACKAGED" && (
                                            <Badge className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold text-[8px] px-1 py-0 h-3.5 border-0 flex items-center gap-0.5 shadow-xs whitespace-nowrap">
                                              <Truck className="w-2 h-2" /> ĐÃ ĐÓNG GÓI
                                            </Badge>
                                          )}
                                        </div>
                                      </TableCell>

                                      <TableCell className="align-middle border-l border-slate-100 px-2 text-[11px] text-slate-700 whitespace-nowrap">
                                        {fmtD(d.deliveryDeadline)}
                                      </TableCell>

                                      {/* Thao tác */}
                                      <TableCell className="text-right pr-4 align-middle">
                                        <div className="flex justify-end">
                                          <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                className="h-8 w-8 p-0"
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                <span className="sr-only">
                                                  Menu Thao tác
                                                </span>
                                                <MoreHorizontal className="h-4 w-4" />
                                              </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent
                                              align="end"
                                              className="w-48"
                                            >
                                              <DropdownMenuItem
                                                onSelect={() => {
                                                  if (d.productId) {
                                                    navigate({
                                                      to: "/products/$productId",
                                                      params: { productId: d.productId },
                                                      search: { returnUrl: `/orders/${orderId}` },
                                                    });
                                                  }
                                                }}
                                              >
                                                <Eye className="mr-2 h-4 w-4" />{" "}
                                                Xem sản phẩm
                                              </DropdownMenuItem>
                                              <DropdownMenuItem
                                                onSelect={() => {
                                                  if (d.productId)
                                                    navigate({
                                                      to: "/products/$productId/edit",
                                                      params: {
                                                        productId: d.productId,
                                                      },
                                                      search: {
                                                        returnUrl: `/orders/${orderId}`,
                                                      },
                                                    });
                                                }}
                                              >
                                                <ExternalLink className="mr-2 h-4 w-4" />{" "}
                                                Sửa sản phẩm
                                              </DropdownMenuItem>

                                              <DropdownMenuItem
                                                onSelect={() => {
                                                  handleUpdateDetailStatus(
                                                    d.id,
                                                    "IN_PRODUCTION",
                                                  );
                                                }}
                                              >
                                                <PlayCircle className="mr-2 h-4 w-4 text-blue-600" />{" "}
                                                <span className="text-blue-600 font-medium">
                                                  Chuyển đang SX
                                                </span>
                                              </DropdownMenuItem>
                                              <DropdownMenuItem
                                                onSelect={() => {
                                                  handleUpdateDetailStatus(
                                                    d.id,
                                                    "COMPLETED",
                                                  );
                                                }}
                                              >
                                                <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-600" />{" "}
                                                <span className="text-emerald-600 font-medium">
                                                  Ép hoàn thành
                                                </span>
                                              </DropdownMenuItem>
                                              <DropdownMenuItem
                                                onSelect={() => handleOpenImposition(d)}
                                              >
                                                <Layers className="mr-2 h-4 w-4 text-purple-600" />
                                                <span className="text-purple-700 font-medium">
                                                  {d.prepressCode ? "Sửa bài bình (Prepress)" : "Bình file (Prepress)"}
                                                </span>
                                              </DropdownMenuItem>
                                              <DropdownMenuItem
                                                onSelect={() => {
                                                  handleUpdateDetailStatus(
                                                    d.id,
                                                    "READY_FOR_PREPRESS",
                                                  );
                                                }}
                                              >
                                                <RotateCcw className="mr-2 h-4 w-4 text-amber-600" />{" "}
                                                <span className="text-amber-700 font-medium">
                                                  Chuyển: Chờ bình file
                                                </span>
                                              </DropdownMenuItem>
                                              <DropdownMenuItem
                                                onSelect={() => {
                                                  if (!d.prepressCode) {
                                                    toast.error("Chi tiết này chưa có thông số bài bình, vui lòng nhập thông số trước!");
                                                    handleOpenImposition(d);
                                                    return;
                                                  }
                                                  handleUpdateDetailStatus(
                                                    d.id,
                                                    "PREPRESS_PLANNED",
                                                  );
                                                }}
                                              >
                                                <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-600" />{" "}
                                                <span className="text-emerald-700 font-medium">
                                                  Xác nhận: Đã bình file
                                                </span>
                                              </DropdownMenuItem>
                                              <DropdownMenuItem
                                                onSelect={() => {
                                                  navigate({
                                                    to: "/delivery/create",
                                                    search: {
                                                      orderId: order.id,
                                                      customerId: order.customerId,
                                                      detailIds: d.id,
                                                    } as any,
                                                  });
                                                }}
                                              >
                                                <Truck className="mr-2 h-4 w-4 text-teal-600" />
                                                <span className="text-teal-700 font-medium">
                                                  Tạo phiếu giao hàng
                                                </span>
                                              </DropdownMenuItem>
                                              <DropdownMenuItem
                                                onSelect={() => {
                                                  navigate({
                                                    to: "/orders/$orderId/edit",
                                                    params: { orderId },
                                                  });
                                                }}
                                              >
                                                <Pencil className="mr-2 h-4 w-4" />{" "}
                                                Sửa chi tiết ĐH
                                              </DropdownMenuItem>
                                              <DropdownMenuItem
                                                className="text-rose-600 focus:text-rose-600 focus:bg-rose-50"
                                                onSelect={() => {
                                                  toast.warning(
                                                    "Vui lòng vào màn hình Sửa ĐH để xoá dòng này!",
                                                  );
                                                }}
                                              >
                                                <Trash2 className="mr-2 h-4 w-4" />{" "}
                                                Xóa sản phẩm
                                              </DropdownMenuItem>
                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                                {/* Totals */}
                                <TableRow className="bg-slate-50 font-bold border-t-2">
                                  <TableCell
                                    colSpan={6}
                                    className="text-right text-xs text-slate-700 px-3 uppercase"
                                  >
                                    Tổng thành tiền (Subtotal)
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums px-2">
                                    {formatNumber(
                                      sorted.reduce(
                                        (s: number, d: any) =>
                                          s + Number(d.quantity || 0),
                                        0,
                                      ),
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums text-amber-600 px-2">
                                    {formatNumber(
                                      sorted.reduce(
                                        (s: number, d: any) =>
                                          s + Number(d.stockQty || 0),
                                        0,
                                      ),
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums text-slate-500 px-2">
                                    {formatNumber(
                                      sorted.reduce(
                                        (s: number, d: any) =>
                                          s +
                                          Math.max(
                                            0,
                                            Math.ceil(
                                              Number(d.quantity || 0) *
                                                (1 +
                                                  (Number(d.allowancePercent) ||
                                                    0) /
                                                    100),
                                            ) - Number(d.quantity || 0),
                                          ),
                                        0,
                                      ),
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums text-blue-700 px-2">
                                    {formatNumber(
                                      sorted.reduce(
                                        (s: number, d: any) =>
                                          s +
                                          Number(d.lsxQty || d.quantity || 0),
                                        0,
                                      ),
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right px-3 text-slate-300">
                                    —
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums px-2 font-bold text-slate-900">
                                    {fmt(lineTotal)}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums text-emerald-600 px-3">
                                    {formatNumber(
                                      sorted.reduce(
                                        (s: number, d: any) =>
                                          s + Number(d.qtyDelivered || 0),
                                        0,
                                      ),
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums text-rose-600 px-3">
                                    {formatNumber(
                                      sorted.reduce(
                                        (s: number, d: any) =>
                                          s +
                                          Math.max(
                                            0,
                                            Number(d.quantity || 0) -
                                              Number(d.qtyDelivered || 0),
                                          ),
                                        0,
                                      ),
                                    )}
                                  </TableCell>
                                  <TableCell colSpan={4} />
                                </TableRow>
                                {vatAmount > 0 && (
                                  <TableRow className="text-xs text-slate-700 font-semibold">
                                    <TableCell
                                      colSpan={11}
                                      className="text-right px-3 uppercase"
                                    >
                                      VAT ({Number(order.vatRate)}%)
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums px-2 border-l border-slate-100">
                                      {fmt(vatAmount)}
                                    </TableCell>
                                    <TableCell colSpan={6} />
                                  </TableRow>
                                )}
                                {shippingFee > 0 && (
                                  <TableRow className="text-xs text-slate-700 font-semibold" data-testid="order-view-shipping-row">
                                    <TableCell
                                      colSpan={11}
                                      className="text-right px-3 uppercase"
                                    >
                                      Chi phí vận chuyển
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums px-2 border-l border-slate-100">
                                      {fmt(shippingFee)}
                                    </TableCell>
                                    <TableCell colSpan={6} />
                                  </TableRow>
                                )}
                                <TableRow className="bg-indigo-50/60 font-bold border-t-2 border-indigo-200">
                                  <TableCell
                                    colSpan={11}
                                    className="text-right text-slate-700 uppercase text-xs px-3"
                                  >
                                    Tổng cộng (Grand Total)
                                  </TableCell>
                                  <TableCell className="text-right text-indigo-600 text-[15px] tabular-nums px-2 border-l border-indigo-200">
                                    {fmt(totalAmount)}
                                  </TableCell>
                                  <TableCell colSpan={6} />
                                </TableRow>
                              </TableBody>
                            </Table>
                          </div>
                        </>
                      )}
                    </>
                  );
                    })()
                  )}
                </div>
              ),
            },
            {
              id: "deliveries",
              label: "Giao hàng",
              icon: <Truck className="h-4 w-4" />,
              content: (
                <div className="p-3 min-h-[400px]">
                  <OrderDeliveriesTab
                    orderId={order.id}
                    customerId={order.customerId}
                    customerName={
                      order.customer?.name || order.customer?.shortName || ""
                    }
                  />
                </div>
              ),
            },
            {
              id: "prepress",
              label: "Chế bản",
              icon: <FileText className="h-4 w-4" />,
              content: (
                <div className="p-3 min-h-[400px]">
                  <OrderPrepressTab
                    order={order}
                    onApproved={() => {
                      queryClient.invalidateQueries({
                        queryKey: ["order", orderId],
                      });
                    }}
                  />
                </div>
              ),
            },
            {
              id: "production",
              label: "Lệnh SX",
              icon: <Factory className="h-4 w-4" />,
              content: (
                <div className="p-3 min-h-[400px]">
                  <OrderProductionOrdersTab
                    orderId={order.id}
                    onOpenCreatePO={() => {
                      setShowCreatePoDialog(true);
                    }}
                  />
                </div>
              ),
            },
            {
              id: "payments",
              label: "Thanh toán",
              icon: <CreditCard className="h-4 w-4" />,
              content: (
                <div className="p-3 min-h-[400px]">
                  <OrderPaymentsTab customerId={order.customerId} />
                </div>
              ),
            },
            {
              id: "settlement",
              label: "Quyết toán & P&L",
              icon: <TrendingUp className="h-4 w-4" />,
              content: (
                <div className="p-3 min-h-[400px]">
                  <OrderSettlementTab
                    orderId={order.id}
                    orderCode={order.orderId}
                    customerId={order.customerId}
                  />
                </div>
              ),
            },
          ]}
        />
      </StandardDetailLayout>

      {/* Print Preview Lightbox */}
      <PrintDialog
        open={showPrint}
        onClose={() => setShowPrint(false)}
        type={printType}
        data={order}
      />

      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lý do hủy đơn hàng</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Vui lòng nhập lý do hủy (Bắt buộc)
            </label>
            <textarea
              className="w-full border border-slate-300 rounded-md p-2 text-sm focus:ring-rose-500 focus:border-rose-500 min-h-[100px]"
              placeholder="Nhập lý do..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
            >
              Quay lại
            </Button>
            <Button
              variant="destructive"
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={!cancelReason.trim()}
              onClick={async () => {
                setShowCancelDialog(false);
                await executeStatusChange(pendingStatus, cancelReason);
              }}
            >
              Xác nhận Hủy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showImpositionModal && impositionDetail && (
        <OrderDetailImpositionModal
          open={showImpositionModal}
          onOpenChange={setShowImpositionModal}
          orderDetail={impositionDetail}
          selectedItems={
            selectedDetailIds.length > 1
              ? details.filter((d: any) => selectedDetailIds.includes(d.id))
              : undefined
          }
          orderCode={order.orderId}
          customerId={order.customerId}
          customerName={order.customer?.name || order.customer?.shortName}
          onSuccess={async () => {
            await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
          }}
        />
      )}

      <RePrepressRequestModal
        open={rePrepressModalOpen}
        onOpenChange={setRePrepressModalOpen}
        items={rePrepressTargetItems}
        onSuccess={async () => {
          await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
        }}
      />

      <ConfirmDialog
        open={showApproveConfirm}
        onOpenChange={setShowApproveConfirm}
        title={`Duyệt đơn hàng ${order.orderId}?`}
        description={
          hasCustomerMissingInfo
            ? `⚠️ CẢNH BÁO: Khách hàng "${order.customer?.shortName || order.customer?.name}" chưa có ${missingCustomerFields.join(" và ")}. Hệ thống sẽ từ chối duyệt đơn hàng này cho đến khi thông tin khách hàng được bổ sung đầy đủ.`
            : "Sau khi duyệt, đơn hàng sẽ chuyển sang trạng thái ĐÃ XÁC NHẬN (CONFIRMED) và sẵn sàng lập Kế hoạch / Lệnh Sản Xuất."
        }
        onConfirm={handleApproveOrder}
        isLoading={isApproving}
        confirmText={hasCustomerMissingInfo ? "Chưa đủ điều kiện duyệt" : "Xác nhận duyệt"}
      />

      {showCreatePoDialog && (
        <CreateProductionOrderDialog
          open={showCreatePoDialog}
          onOpenChange={setShowCreatePoDialog}
          order={order}
          onSuccess={async () => {
            await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
            await queryClient.invalidateQueries({ queryKey: ["orders"] });
          }}
          onOpenImposition={handleOpenImposition}
        />
      )}

      <ZIndexView {...zIndex} />
    </>
  );
}
